import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import theme from './theme';
import Layout from './components/Layout';
import AdminLogin from './components/AdminLogin';
import PatientLogin from './components/PatientLogin';
import Locations from './components/Locations';
import StationConfig from './components/StationConfig';
import PatientList from './components/PatientList';
import PatientRegister from './components/PatientRegister';
import Analytics from './components/Analytics';
import UserManagement from './components/UserManagement';
import AuditLogs from './components/AuditLogs';
import PatientPortal from './components/PatientPortal';
import ReportViewer from './components/ReportViewer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';

function ProtectedRoute({ children, isAuthenticated }) {
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Only accessible by admin/superadmin roles
function AdminRoute({ children, isAuthenticated, user, requiredRole }) {
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (requiredRole && user?.role !== requiredRole && user?.role !== 'superadmin') {
    return <Navigate to="/analytics" replace />;
  }
  return children;
}

function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      try {
        if (isTokenExpired(token)) {
          localStorage.clear();
          return;
        }
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        setIsAuthenticated(true);
      } catch {
        localStorage.clear();
      }
    }
  }, []);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    setIsAuthenticated(false);
    setUser(null);
  };

  const adminRoutes = [
    { path: '/analytics', element: <Analytics /> },
    { path: '/locations', element: <Locations /> },
    { path: '/stations', element: <StationConfig /> },
    { path: '/patients', element: <PatientList /> },
    { path: '/register', element: <PatientRegister /> },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: { borderRadius: '10px', background: '#1e293b', color: '#fff' },
        }}
      />
      <Router>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated
                ? <Navigate to={user?.role === 'patient' ? '/portal' : '/analytics'} replace />
                : <AdminLogin onLogin={handleLogin} />
            }
          />
          <Route
            path="/patient-login"
            element={
              isAuthenticated
                ? <Navigate to="/portal" replace />
                : <PatientLogin onLogin={handleLogin} />
            }
          />

          {/* Short report link — public, no auth */}
          <Route path="/r/:code" element={<ReportViewer />} />

          {/* Legal pages — public, no auth */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />

          {/* Patient OTP Portal — no sidebar */}
          <Route
            path="/portal"
            element={
              isAuthenticated
                ? <PatientPortal onLogout={handleLogout} />
                : <Navigate to="/patient-login" replace />
            }
          />

          <Route path="/" element={<Navigate to={isAuthenticated ? '/analytics' : '/login'} replace />} />
          <Route path="/dashboard" element={<Navigate to="/analytics" replace />} />

          {/* Admin routes with sidebar Layout */}
          {adminRoutes.map(({ path, element }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <Layout onLogout={handleLogout} user={user}>
                    {element}
                  </Layout>
                </ProtectedRoute>
              }
            />
          ))}

          {/* SuperAdmin-only routes */}
          <Route
            path="/users"
            element={
              <AdminRoute isAuthenticated={isAuthenticated} user={user} requiredRole="superadmin">
                <Layout onLogout={handleLogout} user={user}>
                  <UserManagement />
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <AdminRoute isAuthenticated={isAuthenticated} user={user} requiredRole="superadmin">
                <Layout onLogout={handleLogout} user={user}>
                  <AuditLogs />
                </Layout>
              </AdminRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
